import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { LogOut, KeyRound, Shield } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import PushSettings from "@/components/PushSettings";
import MetaConnectCard from "@/components/MetaConnectCard";

export default function ProfilePage() {
  const { user, logout, refresh } = useAuth();
  const qc = useQueryClient();
  const [form, setForm] = useState({ name: user?.name || "", phone: user?.phone || "" });
  const [pw, setPw] = useState({ password: "", confirm: "" });

  const saveMut = useMutation({
    mutationFn: async () => (await api.patch(`/users/${user.id}`, form)).data,
    onSuccess: () => {
      toast.success("Profile updated");
      refresh();
      qc.invalidateQueries({ queryKey: ["users"] });
    },
  });

  const pwMut = useMutation({
    mutationFn: async () => (await api.patch(`/users/${user.id}`, { password: pw.password })).data,
    onSuccess: () => {
      toast.success("Password updated");
      setPw({ password: "", confirm: "" });
    },
    onError: () => toast.error("Failed to update password"),
  });

  if (!user) return null;

  return (
    <div className="space-y-5" data-testid="profile-page">
      <div className="soft-card flex flex-col items-center p-6 text-center sm:flex-row sm:items-center sm:text-left">
        <Avatar className="h-16 w-16 bg-[hsl(var(--brand-navy))]">
          <AvatarFallback className="bg-[hsl(var(--brand-navy))] text-lg text-white">
            {user.initials || user.name[0]}
          </AvatarFallback>
        </Avatar>
        <div className="sm:ml-4">
          <div className="flex items-center justify-center gap-2 sm:justify-start">
            <h2 className="text-xl font-bold text-navy">{user.name}</h2>
            {user.role === "admin" && (
              <Badge className="border-transparent bg-amber-100 text-[10px] font-semibold text-amber-800 hover:bg-amber-100">
                <Shield className="mr-1 h-2.5 w-2.5" /> Admin
              </Badge>
            )}
          </div>
          <div className="mt-1 text-sm text-slate-500">{user.email}</div>
          {user.phone && <div className="text-xs text-slate-500">{user.phone}</div>}
        </div>
      </div>

      <section className="soft-card p-4 sm:p-6" data-testid="profile-details">
        <h3 className="mb-3 text-sm font-bold text-navy">Personal details</h3>
        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <Label className="text-xs">Full name</Label>
            <Input
              data-testid="profile-name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
            />
          </div>
          <div>
            <Label className="text-xs">Phone</Label>
            <Input
              data-testid="profile-phone"
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
            />
          </div>
        </div>
        <Button
          data-testid="profile-save"
          className="mt-4 bg-navy hover:brightness-110"
          disabled={saveMut.isPending}
          onClick={() => saveMut.mutate()}
        >
          Save changes
        </Button>
      </section>

      <section className="soft-card p-4 sm:p-6" data-testid="profile-security">
        <h3 className="mb-3 flex items-center gap-2 text-sm font-bold text-navy">
          <KeyRound className="h-4 w-4" /> Change password
        </h3>
        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <Label className="text-xs">New password</Label>
            <Input
              data-testid="profile-new-password"
              type="password"
              value={pw.password}
              onChange={(e) => setPw({ ...pw, password: e.target.value })}
            />
          </div>
          <div>
            <Label className="text-xs">Confirm</Label>
            <Input
              data-testid="profile-confirm-password"
              type="password"
              value={pw.confirm}
              onChange={(e) => setPw({ ...pw, confirm: e.target.value })}
            />
          </div>
        </div>
        <Button
          data-testid="profile-update-password"
          variant="outline"
          className="mt-4"
          disabled={
            !pw.password ||
            pw.password.length < 6 ||
            pw.password !== pw.confirm ||
            pwMut.isPending
          }
          onClick={() => pwMut.mutate()}
        >
          Update password
        </Button>
      </section>

      <PushSettings />
      {user.role === "admin" && <MetaConnectCard />}

      <Button
        data-testid="profile-logout"
        variant="destructive"
        className="w-full"
        onClick={logout}
      >
        <LogOut className="h-4 w-4" /> Sign out
      </Button>
    </div>
  );
}
